/** The message box at the foot of a run page, with Send beside it. One component for
 * a conversation and a scheduled run, so the two pages cannot drift apart. */
import type { Ref } from "react";

import type { Attachments } from "../api/hooks/useAttachments";
import { sendOnEnter } from "../utils/sendShortcut";
import { AttachButton, AttachedFiles } from "./AttachmentsBar";
import styles from "./RunComposer.module.css";

export const RunComposer = ({
  value,
  onChange,
  onSend,
  placeholder,
  disabled,
  sending,
  textareaRef,
  attachments,
  queueing = false,
}: {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  placeholder: string;
  /** The agent is working: nothing can be sent until it finishes. The box stays
   * editable, so a note can be written for the next message meanwhile. */
  disabled: boolean;
  sending: boolean;
  textareaRef: Ref<HTMLTextAreaElement> | null;
  /** Files going with the message; the caller sends ``attachments.ready``. */
  attachments: Attachments;
  /** The agent is working, and what is sent waits for it to finish: Send queues. */
  queueing?: boolean;
}) => {
  const blocked =
    disabled || sending || !value.trim() || attachments.uploading || attachments.failed;
  return (
    <div className={styles.stack}>
      <div className={styles.composerInline}>
        <textarea
          ref={textareaRef ?? undefined}
          className={styles.composer}
          rows={3}
          placeholder={placeholder}
          value={value}
          title={queueing ? "Sent when the agent has finished this turn" : undefined}
          onChange={(e) => onChange(e.target.value)}
          onPaste={sending ? undefined : attachments.onPaste}
          onKeyDown={sendOnEnter(() => {
            if (!blocked) onSend();
          })}
        />
        <div className={styles.actions}>
          <AttachButton attachments={attachments} disabled={disabled || sending} />
          <button type="button" className={styles.primary} disabled={blocked} onClick={onSend}>
            {sending
              ? queueing
                ? "Queueing…"
                : "Sending…"
              : attachments.uploading
                ? "Uploading…"
                : queueing
                  ? "Queue"
                  : "Send"}
          </button>
        </div>
      </div>
      {(attachments.items.length > 0 || attachments.rejection) && (
        <div className={styles.attached}>
          <AttachedFiles attachments={attachments} disabled={disabled || sending} />
        </div>
      )}
    </div>
  );
};
